import { createT, type Translator } from '@freightapi/i18n'
import { resolveLocale } from '@freightapi/i18n/locale'
import { LOCALES, type Locale } from '@freightapi/i18n/namespaces'
import { UiI18nProvider } from '@freightapi/ui'
import {
  createContext,
  type ReactNode,
  use,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'

const STORAGE_KEY = 'cargofollow.console.locale'

export interface ConsoleI18n {
  readonly locale: Locale
  readonly t: Translator<'console'>
  /**
   * The catalogue of the shared components (M3-04). `ShipmentView`, `Timeline` and `DocumentList`
   * are authored against `sign`, so a console screen that renders one of them needs those words
   * too — the alternative, copying event names and box labels into the console catalogue, is the
   * duplication ADR 0010 exists to prevent.
   */
  readonly sign: Translator<'sign'>
  readonly setLocale: (locale: Locale) => void
}

const ConsoleI18nContext = createContext<ConsoleI18n | null>(null)

/**
 * Locale negotiation in the order ADR 0010 prescribes: `?locale=` first, then what the developer
 * picked before, then the browser's languages.
 */
export function initialLocale(): Locale {
  if (typeof window === 'undefined') return 'nl'
  const stored = window.localStorage.getItem(STORAGE_KEY)
  return resolveLocale({
    query: new URLSearchParams(window.location.search).get('locale'),
    acceptLanguage: [stored, ...navigator.languages].filter(Boolean).join(','),
    supported: LOCALES,
  })
}

export function ConsoleI18nProvider({ children }: { readonly children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(initialLocale)

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next)
    try {
      window.localStorage.setItem(STORAGE_KEY, next)
    } catch {
      // Private mode or a full quota: the choice simply does not survive the session.
    }
  }, [])

  useEffect(() => {
    document.documentElement.lang = locale
  }, [locale])

  const value = useMemo<ConsoleI18n>(
    () => ({ locale, t: createT('console', locale), sign: createT('sign', locale), setLocale }),
    [locale, setLocale],
  )

  return (
    <ConsoleI18nContext value={value}>
      <UiI18nProvider locale={locale} translator={value.sign}>
        {children}
      </UiI18nProvider>
    </ConsoleI18nContext>
  )
}

export function useConsoleI18n(): ConsoleI18n {
  const value = use(ConsoleI18nContext)
  if (value === null) throw new Error('useConsoleI18n must be used inside <ConsoleI18nProvider>')
  return value
}
